import { Card } from "@omnichannel/ui";
import type { RevenueSnapshotData } from "./api";

const integerMinor = /^-?\d+$/;

/** Format integer minor units directly, without floating-point money arithmetic. */
export function formatSnapshotMoney(minor: string, currency: string): string {
  if (!integerMinor.test(minor)) throw new Error("Invalid integer minor units");
  const value = BigInt(minor);
  const absolute = value < 0n ? -value : value;
  const whole = (absolute / 100n).toLocaleString("ru-RU");
  const fraction = String(absolute % 100n).padStart(2, "0");
  return `${value < 0n ? "−" : ""}${whole},${fraction} ${currency}`;
}

function completeSnapshot(value: unknown): value is RevenueSnapshotData {
  if (!value || typeof value !== "object") return false;
  const snapshot = value as Partial<RevenueSnapshotData>;
  const amounts = snapshot.amounts_minor_by_currency;
  const counts = [
    snapshot.active_paid_subscriptions,
    snapshot.missing_amount_subscriptions,
    snapshot.missing_period_subscriptions,
  ];
  if (
    !counts.every(
      (count) => typeof count === "number" && Number.isSafeInteger(count) && count >= 0,
    ) ||
    !amounts ||
    typeof amounts !== "object" ||
    Array.isArray(amounts)
  ) return false;
  const knownAmountCount =
    snapshot.active_paid_subscriptions! - snapshot.missing_amount_subscriptions!;
  const currencyCount = Object.keys(amounts).length;
  return (
    snapshot.metric === "latest_active_payment_snapshot" &&
    snapshot.mrr_status === "billing_periods_unavailable" &&
    snapshot.normalized_mrr_minor_by_currency === null &&
    knownAmountCount >= 0 &&
    snapshot.missing_period_subscriptions === snapshot.active_paid_subscriptions &&
    currencyCount <= knownAmountCount &&
    (knownAmountCount > 0) === (currencyCount > 0) &&
    Object.entries(amounts).every(
      ([currency, amount]) =>
        currency.trim().length > 0 &&
        typeof amount === "string" &&
        integerMinor.test(amount),
    )
  );
}

export function RevenueSnapshot({
  snapshot,
  loading = false,
}: {
  snapshot?: RevenueSnapshotData | null;
  loading?: boolean;
}) {
  const available = completeSnapshot(snapshot);
  const amounts = available
    ? Object.entries(snapshot.amounts_minor_by_currency)
    : [];
  return (
    <Card className="metric-card">
      <section aria-label="Последние платежи активных подписок">
        <h2>Последние платежи активных подписок</h2>
        <p>MRR недоступен: нет данных о периодах оплаты</p>
        {loading ? (
          <p role="status">Загрузка сведений о платежах…</p>
        ) : !available ? (
          <p>Данные недоступны</p>
        ) : (
          <>
            {amounts.length ? (
              <ul>
                {amounts.map(([currency, amount]) => (
                  <li key={currency}>{formatSnapshotMoney(amount, currency)}</li>
                ))}
              </ul>
            ) : (
              <p>Нет сумм платежей</p>
            )}
            <p>Активных подписок с оплатой: {snapshot.active_paid_subscriptions}.</p>
            {snapshot.missing_amount_subscriptions > 0 && (
              <p>
                Не указана сумма платежа: {snapshot.missing_amount_subscriptions}.
                {" "}Показаны неполные суммы.
              </p>
            )}
            <p>Период оплаты не указан: {snapshot.missing_period_subscriptions}.</p>
            {amounts.some(([, amount]) => BigInt(amount) < 0n) && (
              <p>В истории есть отрицательная сумма — проверьте платежи.</p>
            )}
            <p>
              Суммы взяты из последних платежей. Возвраты и расходы здесь не учтены;
              пересчёта на месяц нет.
            </p>
          </>
        )}
      </section>
    </Card>
  );
}
